<script setup>
import { computed, h, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import Dialog from 'next/dialog/Dialog.vue';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');

const { isImpersonating } = useImpersonation();

const offlineConfirmDialogRef = ref(null);
const conversationsCount = ref(0);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

async function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }

  // Si el usuario está cambiando a offline desde online, verificar conversaciones
  if (
    availability === 'offline' &&
    currentUserAvailability.value === 'online'
  ) {
    try {
      const count = await store.dispatch(
        'checkConversationsBeforeOffline',
        currentAccountId.value
      );
      conversationsCount.value = count;

      if (count > 0) {
        // Mostrar diálogo de confirmación
        offlineConfirmDialogRef.value?.open();
        return;
      }
    } catch (error) {
      // Si hay error al verificar, continuar con el cambio normal
    }
  }

  // Cambiar estado normalmente para otros casos
  updateAvailabilityStatus(availability);
}

function updateAvailabilityStatus(availability) {
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}

async function confirmGoOffline() {
  console.log('confirmGoOffline called');
  try {
    // Reasignar conversaciones
    console.log(
      'Dispatching reassignConversationsOnDisconnect with accountId:',
      currentAccountId.value
    );
    const result = await store.dispatch(
      'reassignConversationsOnDisconnect',
      currentAccountId.value
    );
    console.log('Reassignment result:', result);

    // Verificar si no hay agentes disponibles
    if (result && result.no_agents_available) {
      // Cerrar el diálogo
      offlineConfirmDialogRef.value?.close();

      // Mostrar mensaje de error
      useAlert(
        t('PROFILE_SETTINGS.FORM.AVAILABILITY.NO_AGENTS_AVAILABLE', {
          count: conversationsCount.value,
        })
      );
      return; // No cambiar el estado del agente
    }

    const reassignedCount = result?.reassigned_count || result || 0;

    // Cambiar estado a offline
    console.log('Updating availability to offline');
    updateAvailabilityStatus('offline');

    // Cerrar el diálogo
    offlineConfirmDialogRef.value?.close();

    // Mostrar mensaje de éxito
    useAlert(
      t('PROFILE_SETTINGS.FORM.AVAILABILITY.REASSIGN_SUCCESS', {
        count: reassignedCount,
      })
    );
  } catch (error) {
    console.error('Error in confirmGoOffline:', error);
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.REASSIGN_ERROR'));
  }
}

function updateAutoOffline(autoOffline) {
  store.dispatch('updateAutoOffline', {
    accountId: currentAccountId.value,
    autoOffline,
  });
}
</script>

<template>
  <DropdownSection>
    <div class="grid gap-0">
      <DropdownItem preserve-open>
        <div class="flex-grow flex items-center gap-1">
          {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
        </div>
        <DropdownContainer>
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              class="min-w-[96px]"
              icon="i-lucide-chevron-down"
              trailing-icon
              @click="toggle"
            >
              <div class="flex gap-1 items-center flex-grow text-sm">
                <div class="p-1 flex-shrink-0">
                  <div class="size-2 rounded-sm" :class="activeStatus.color" />
                </div>
                <span>{{ activeStatus.label }}</span>
              </div>
            </Button>
          </template>
          <DropdownBody class="min-w-32 z-20">
            <DropdownItem
              v-for="status in availabilityStatuses"
              :key="status.value"
              :label="status.label"
              :icon="status.icon"
              class="cursor-pointer"
              @click="changeAvailabilityStatus(status.value)"
            />
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>
      <DropdownItem>
        <div class="flex-grow flex items-center gap-1">
          {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
          <Icon
            v-tooltip.top="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT')"
            icon="i-lucide-info"
            class="size-4 text-n-slate-10"
          />
        </div>
        <woot-switch
          class="flex-shrink-0"
          :model-value="currentUserAutoOffline"
          @input="updateAutoOffline"
        />
      </DropdownItem>
    </div>
  </DropdownSection>

  <Dialog
    ref="offlineConfirmDialogRef"
    type="alert"
    :title="
      $t('PROFILE_SETTINGS.FORM.AVAILABILITY.OFFLINE_CONFIRM_DIALOG.TITLE')
    "
    :description="
      $t(
        'PROFILE_SETTINGS.FORM.AVAILABILITY.OFFLINE_CONFIRM_DIALOG.DESCRIPTION',
        { count: conversationsCount }
      )
    "
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="
            $t(
              'PROFILE_SETTINGS.FORM.AVAILABILITY.OFFLINE_CONFIRM_DIALOG.CANCEL'
            )
          "
          class="w-full"
          type="button"
          @click="offlineConfirmDialogRef?.close()"
        />
        <Button
          color="ruby"
          :label="
            $t(
              'PROFILE_SETTINGS.FORM.AVAILABILITY.OFFLINE_CONFIRM_DIALOG.CONFIRM'
            )
          "
          class="w-full"
          type="button"
          @click="confirmGoOffline"
        />
      </div>
    </template>
  </Dialog>
</template>
